// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { xBaseTheme } from "@steiner/XDL/types";
import { color } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

interface CheckboxStyleProps {
    background: string;
    borderColor: string;
}

const checkboxStyle: {
    default: CheckboxStyleProps;
    defaultHover: CheckboxStyleProps;
    checked: CheckboxStyleProps;
    checkedHover: CheckboxStyleProps;
} = {
    default: {
        background: "common.ui2",
        borderColor: "common.ui4",
    },
    defaultHover: {
        background: "common.ui2",
        borderColor: "common.ui9",
    },
    checked: {
        background: "common.ui10",
        borderColor: "common.ui4",
    },
    checkedHover: {
        background: "common.ui9",
        borderColor: "common.ui9",
    },
};

interface CheckboxSizesProps {
    fontSize: number;
    iconSize: number;
    borderRadius: number;
}

export const checkboxSizes: {
    [key: string]: CheckboxSizesProps;
} = {
    default: {
        fontSize: 13,
        iconSize: 13,
        borderRadius: 1,
    },
    small: {
        fontSize: 10,
        iconSize: 12,
        borderRadius: 1,
    },
};

export default {
    defaultProps: {
        ...CheckboxPresentationPropsDefault,
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: 12,
            css: css`
                letter-spacing: 0;
                line-height: 15px;
            `,
        },
        iconProps: {
            src: "Check",
            size: 13,
        },
        indeterminateIconProps: {
            src: "Minus",
            size: 13,
        },
        css: css`
            cursor: ${({ disabled }: any) => (disabled ? "not-allowed" : "pointer")};

            span[role="checkbox"] {
                transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

                border-radius: ${({ _sizeVariant }: any) => checkboxSizes[_sizeVariant].borderRadius}px;
                min-width: ${({ _sizeVariant }: any) => checkboxSizes[_sizeVariant].iconSize}px;
                border: 1px solid ${color(checkboxStyle.default.borderColor)};
                background-color: ${color(checkboxStyle.default.background)};
                cursor: pointer;
                padding: 1px;

                &[aria-disabled="true"] {
                    cursor: not-allowed;
                }

                &[aria-checked="true"]:not([aria-disabled="true"]),
                &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                    background: ${color(checkboxStyle.checked.background)};
                    border-color: ${color(checkboxStyle.checked.borderColor)};
                }
                & + label {
                    margin-left: 6px;
                }
                &:focus {
                    outline: none;
                    box-shadow: none;
                }
                & + label {
                    margin-left: 6px;
                }
            }

            &:hover {
                span[role="checkbox"] {
                    &[aria-checked="true"]:not([aria-disabled="true"]),
                    &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                        background: ${color(checkboxStyle.checkedHover.background)};
                        border-color: ${color(checkboxStyle.checkedHover.borderColor)};
                    }
                    &[aria-checked="false"]:not([aria-disabled="true"]) {
                        background: ${color(checkboxStyle.defaultHover.background)};
                        border-color: ${color(checkboxStyle.defaultHover.borderColor)};
                    }
                }
            }

            &:focus {
                span[role="checkbox"] {
                    outline: none;
                    box-shadow: none;
                }
            }
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        labelProps: {
            size: 14,
            weight: "bold",
        },
        css: css`
            & > ${TypographyStyle}:empty {
                display: none;
            }
        `,
    },
} as xBaseTheme["checkbox"];
